import { getPost } from '@/lib/content';
import PageMeta from '@/shared/components/PageMeta';
import NotFoundPage from '@/pages/NotFoundPage';
import AcademicLayout from '@/shared/components/AcademicLayout';
import Markdown from '@/shared/components/Markdown';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { formatDate } from '@/shared/utils/i18n';
import ExternalLink from '@/shared/ui/external-link';
import { ArrowLeft, ArrowUpRight, Calendar } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

export default function PostDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { language, t } = useLanguage();

  const post = slug ? getPost(slug, language) : undefined;

  if (!post) {
    return <NotFoundPage />;
  }

  return (
    <AcademicLayout showQuickLinks={false}>
      <PageMeta title={post.meta.title} description={post.meta.summary} path={`/posts/${post.slug}`} />
      <article className="space-y-8 max-w-3xl">
        <div>
          <Link
            to="/posts"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('backToPosts')}
          </Link>

          <h1 className="text-3xl font-light text-foreground mb-4">{post.meta.title}</h1>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(post.meta.date, language)}
            </span>
            {post.meta.category && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {post.meta.category}
              </span>
            )}
            {post.meta.tags.map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 rounded-md text-xs bg-muted/40 text-muted-foreground border border-border/30"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <Markdown>{post.body}</Markdown>

        {(post.meta.links?.external || post.meta.links?.github || post.meta.links?.pdf) && (
          <div className="flex flex-wrap gap-4 pt-4 border-t border-border/50">
            {post.meta.links.external && (
              <ExternalLink
                href={post.meta.links.external}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                {t('externalLink')}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </ExternalLink>
            )}
            {post.meta.links.github && (
              <ExternalLink
                href={post.meta.links.github}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                GitHub
                <ArrowUpRight className="w-3.5 h-3.5" />
              </ExternalLink>
            )}
            {post.meta.links.pdf && (
              <ExternalLink
                href={post.meta.links.pdf}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                PDF
                <ArrowUpRight className="w-3.5 h-3.5" />
              </ExternalLink>
            )}
          </div>
        )}
      </article>
    </AcademicLayout>
  );
}
